import React from 'react';
import { AlertOctagon } from 'lucide-react';
import SystemPage from '@parsina/framework/clinical/src/components/system/SystemPage';
import { C, RADIUS } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// Crash containment.
//
// Until this existed, one thrown exception anywhere in the render tree unmounted
// the entire app and left a white page — no message, no way back, no clue. In a
// clinical tool that is the worst possible failure mode: it happens mid-visit,
// it looks identical to a dead laptop, and the doctor's only recourse is a
// browser refresh they have to think of themselves. (The bug that motivated
// this was real, and there were two of them: `ErrorState` and `ConfirmDialog`
// each referenced `t` in a default parameter, so omitting the `title` prop
// threw a ReferenceError — an error renderer and a confirmation dialog that
// crashed the app when asked to do their job. Both are fixed; the boundary is
// what makes the next one a bad page instead of a blank one.)
//
// Two boundaries, at two altitudes, because they answer different questions:
//
//   ScreenErrorBoundary  wraps the routed screen INSIDE the app chrome. The
//                        sidebar, the nav and the doctor's context all survive,
//                        so the answer to "one page broke" is "use another
//                        page" — a nuisance, not an outage. Resets itself on
//                        navigation, so leaving the broken route is enough.
//
//   AppErrorBoundary     wraps everything, including the chrome. It only ever
//                        catches what the inner one could not — a crash in the
//                        layout, the providers, or the boundary's own fallback.
//                        Reload is the only honest offer at that altitude.
//
// Neither swallows the error: both log it, so it still reaches whatever console
// or error collector is watching. A boundary that hides the stack trades a
// visible bug for an invisible one.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The mechanism. Deliberately dumb and dependency-free — no hooks, no i18n, no
 * router — because it must survive the failure of the things around it. All
 * copy and all actions arrive as props from the wrappers below.
 *
 * `resetKey` is how a boundary recovers: React keeps a boundary latched in its
 * error state forever once it has caught something, so without an external
 * signal the doctor stays stuck on the fallback even after navigating away.
 * Changing the key (the wrappers pass the pathname) clears the latch.
 */
export class ErrorBoundaryCore extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack: `error.stack` alone points at the component
    // that threw, not at the tree that rendered it, and for a shared primitive
    // (Button, Card, ErrorState) the latter is the only part that identifies
    // WHICH screen is broken.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return this.props.renderFallback(this.state.error, this.reset);
  }
}

/**
 * The routed-screen boundary. Import this, not the core.
 *
 * Mount it INSIDE the layout and give it the pathname as `resetKey`.
 */
export function ScreenErrorBoundary({ children, resetKey, onGoHome }) {
  const { t } = useTranslation();

  const renderFallback = (error, reset) => (
    <SystemPage
      testId="screen-error"
      tone="danger"
      icon={<AlertOctagon size={30} strokeWidth={2} aria-hidden />}
      title={t('errors.crash.screenTitle')}
      body={t('errors.crash.screenBody')}
      // The message, not the stack. A doctor cannot read a stack, and the
      // message is the part that lets support reproduce it. The stack is in
      // the console for whoever goes looking.
      detail={error?.message || String(error)}
      actions={[
        { key: 'retry', label: t('errors.crash.retryScreen'), onClick: reset, variant: 'primary', testId: 'screen-error-retry' },
        ...(onGoHome ? [{ key: 'home', label: t('errors.crash.goHome'), onClick: onGoHome, variant: 'secondary' }] : []),
      ]}
      footer={t('errors.crash.dataSafe')}
    />
  );

  return (
    <ErrorBoundaryCore resetKey={resetKey} renderFallback={renderFallback}>
      {children}
    </ErrorBoundaryCore>
  );
}

/**
 * The outermost boundary. Mount it around the whole app, above the router.
 *
 * It cannot offer "go to another page" — at this altitude the router may be
 * exactly what is broken — so a full reload is the primary action. It is also
 * the reason this fallback stays fullscreen: there is no chrome left to sit in.
 */
export function AppErrorBoundary({ children }) {
  const { t } = useTranslation();

  const renderFallback = (error) => (
    <SystemPage
      testId="app-error"
      fullscreen
      tone="danger"
      icon={<AlertOctagon size={30} strokeWidth={2} aria-hidden />}
      title={t('errors.crash.appTitle')}
      body={t('errors.crash.appBody')}
      detail={error?.message || String(error)}
      actions={[
        {
          key: 'reload',
          label: t('errors.crash.reload'),
          // `location.reload()` rather than a router reset: the state that
          // produced the crash is still in memory, and re-rendering into it
          // reproduces the crash immediately. A reload is the only reset that
          // is actually a reset.
          onClick: () => window.location.reload(),
          variant: 'primary',
          testId: 'app-error-reload',
        },
      ]}
      footer={t('errors.crash.dataSafe')}
    />
  );

  return <ErrorBoundaryCore renderFallback={renderFallback}>{children}</ErrorBoundaryCore>;
}

// ── The last resort ──────────────────────────────────────────────────────────
// `AppErrorBoundary` reads its copy through `t`, which means it has to sit
// INSIDE I18nProvider — and a boundary inside a provider cannot catch that
// provider throwing. So one more boundary wraps the providers themselves.
//
// It cannot use `t` (that is the whole point), so its copy is static, in all
// five panel languages — its direction too, as data on each entry — and it
// picks one from what the pre-paint script in index.html already set on
// <html lang> (from the same `parsina.locale` cache I18nProvider reads) — the
// same compromise, for the same reason, as the service worker's offline page. It should realistically never render: the
// providers are three lines of context each. But "should never happen" is not
// a rendering strategy, and the alternative is the white screen again.

// i18n-exempt: last-resort boundary — renders when I18nProvider itself has failed, so `t` is unavailable.
const FATAL_FA = { title: 'خطای غیرمنتظره', body: 'برنامه با مشکلی مواجه شد. صفحه را دوباره بارگذاری کنید.', action: 'بارگذاری مجدد', dir: 'rtl' };
// i18n-exempt: last-resort boundary — renders when I18nProvider itself has failed, so `t` is unavailable.
const FATAL_EN = { title: 'Unexpected error', body: 'The application ran into a problem. Please reload the page.', action: 'Reload', dir: 'ltr' };
// i18n-exempt: last-resort boundary — renders when I18nProvider itself has failed, so `t` is unavailable.
const FATAL_AR = { title: 'خطأ غير متوقع', body: 'واجه التطبيق مشكلة. يُرجى إعادة تحميل الصفحة.', action: 'إعادة التحميل', dir: 'rtl' };
// i18n-exempt: last-resort boundary — renders when I18nProvider itself has failed, so `t` is unavailable.
const FATAL_FR = { title: 'Erreur inattendue', body: 'L’application a rencontré un problème. Veuillez recharger la page.', action: 'Recharger', dir: 'ltr' };
// i18n-exempt: last-resort boundary — renders when I18nProvider itself has failed, so `t` is unavailable.
const FATAL_DE = { title: 'Unerwarteter Fehler', body: 'In der Anwendung ist ein Problem aufgetreten. Bitte laden Sie die Seite neu.', action: 'Neu laden', dir: 'ltr' };
const FATAL = { fa: FATAL_FA, en: FATAL_EN, ar: FATAL_AR, fr: FATAL_FR, de: FATAL_DE };

/** The panel language without the i18n runtime: <html lang>, then the cache, then Persian. */
function fatalLanguage() {
  const narrow = (v) => {
    const base = String(v || '').toLowerCase().split(/[-_]/)[0];
    return FATAL[base] ? base : null;
  };
  try {
    return narrow(document.documentElement.getAttribute('lang'))
      || narrow(window.localStorage.getItem('parsina.locale'))
      || 'fa';
  } catch {
    return 'fa';
  }
}

export function RootErrorBoundary({ children }) {
  // Tokens, not literal hex — including here. `C.*` is a plain module import,
  // not React context, and the accent variables come from tokens.css with
  // `:root` defaults, so both survive exactly the failure this boundary exists
  // for. Only the COPY has to be static; the paint does not, and hardcoding it
  // would leave one screen in the product that ignores the doctor's accent.
  //
  // The type scale is not used here for the opposite reason: `type` scales with
  // the `data-font-size` attribute ThemeProvider stamps, and this renders when
  // ThemeProvider may never have run. Literal rem is the honest choice at this
  // one altitude, and the values match `type.metric` / `body` / `caption` at
  // the default setting.
  const renderFallback = (error) => {
    const lang = fatalLanguage();
    const copy = FATAL[lang];
    return (
      <div
        dir={copy.dir}
        lang={lang}
        role="alert"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100vw', height: '100vh', margin: 0,
          background: C.bg, fontFamily: 'var(--app-font)',
          padding: '1.25rem', boxSizing: 'border-box',
        }}
      >
        <div style={{
          background: C.card, borderRadius: RADIUS.card, border: `1px solid ${C.border}`,
          padding: '2.25rem 2rem', maxWidth: '27.5rem', width: '100%', textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.1875rem', fontWeight: 800, lineHeight: 1.2, color: C.textPrimary, marginBottom: '0.5rem' }}>{copy.title}</div>
          <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: C.textSecondary, lineHeight: 1.85, marginBottom: '1.375rem' }}>{copy.body}</div>
          {/* A raw <button>, deliberately and only here: the kit Button is a
              Tailwind-classed component, and this fallback has to work even if
              the failure it caught was in the component layer itself. It is
              styled to match `Button` at its default size. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="root-error-reload"
            style={{
              height: '2.75rem', padding: '0 1.25rem', borderRadius: RADIUS.control,
              border: 'none', background: C.navy, color: '#fff',
              fontWeight: 700, fontSize: '0.84375rem',
              fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {copy.action}
          </button>
          {error?.message && (
            <div dir="ltr" style={{
              marginTop: '1.25rem', fontSize: '0.6875rem', color: C.textMuted, fontFamily: 'monospace',
              wordBreak: 'break-word', textAlign: 'left',
            }}>
              {String(error.message)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return <ErrorBoundaryCore renderFallback={renderFallback}>{children}</ErrorBoundaryCore>;
}

export default AppErrorBoundary;
